import React, { useEffect, useState, createContext, useContext } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useProperty } from "@/lib/property";
import { Navigate } from "react-router-dom";

const PermCtx = createContext({ role: null, sections: {}, loading: true });

// section key list — must stay in sync with backend PERMISSION_SECTIONS
export const PERMISSION_SECTIONS = [
  "dashboard", "front_desk", "reservations", "rate_calendar", "rooms", "guests",
  "housekeeping", "invoices", "channels", "properties", "staff", "attendance",
  "payroll", "expenses", "pos", "reports", "audit", "admin", "help",
  "halls", "amenities",
];

export const SECTION_LABEL = {
  dashboard: "Dashboard",
  front_desk: "Front Desk",
  reservations: "Reservations",
  rate_calendar: "Rate Calendar",
  rooms: "Rooms",
  guests: "Guests",
  housekeeping: "Housekeeping",
  invoices: "Invoices",
  channels: "Channels",
  properties: "Properties",
  staff: "Staff & Access",
  attendance: "Attendance",
  payroll: "Payroll",
  expenses: "Expenses",
  pos: "Restaurant POS",
  reports: "Reports",
  audit: "Audit history",
  admin: "Admin Console",
  help: "Help",
  halls: "Halls & Events",
  amenities: "Amenities",
};

const LEVEL_ORDER = { none: 0, read: 1, write: 2 };

export function PermissionProvider({ children }) {
  const { user, setUser } = useAuth();
  const { activeId } = useProperty() || {};
  const [state, setState] = useState({ role: null, sections: {}, loading: true });

  useEffect(() => {
    if (!user) { setState({ role: null, sections: {}, loading: false }); return; }
    api.get("/auth/permissions", { silent: true }).then((r) => {
      setState({ ...r.data, loading: false });
      if (r.data.role && r.data.role !== user.role) setUser({ ...user, role: r.data.role });
    }).catch(() => setState({ role: user.role, sections: {}, loading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, activeId]);

  return <PermCtx.Provider value={state}>{children}</PermCtx.Provider>;
}

export function usePermissions() {
  return useContext(PermCtx);
}

// Convenience hook — returns effective level + booleans for a section
export function usePermission(section) {
  const { sections, role, loading } = usePermissions();
  const level = role === "admin" ? "write" : (sections?.[section] || "none");
  return {
    level,
    canRead: LEVEL_ORDER[level] >= 1,
    canWrite: LEVEL_ORDER[level] >= 2,
    loading,
  };
}

// Route-level guard — wrap around any protected page element in App routes.
// If the user's section permission is `none`, redirect to their first accessible page.
export function RequireSection({ section, children }) {
  const { sections, role, loading } = usePermissions();
  if (loading) return <div className="min-h-screen flex items-center justify-center label-overline">Loading…</div>;
  if (role === "admin") return children;
  const level = sections?.[section];
  if (level && level !== "none") return children;
  // Not allowed — send them to a landing page they CAN see.
  // Prefer pos → dashboard → help order so restaurant_manager lands on /pos.
  const preferred = ["pos", "dashboard", "front_desk", "reservations", "housekeeping", "invoices", "help"];
  const fallback = preferred.find((s) => sections?.[s] && sections[s] !== "none");
  const routes = {
    dashboard: "/dashboard", front_desk: "/timeline", reservations: "/reservations",
    rate_calendar: "/rate-calendar", rooms: "/rooms", guests: "/guests",
    housekeeping: "/housekeeping", invoices: "/invoices", channels: "/channels",
    properties: "/properties", staff: "/staff", attendance: "/attendance",
    payroll: "/payroll", expenses: "/expenses", pos: "/pos", reports: "/reports",
    audit: "/audit", admin: "/admin", help: "/help", halls: "/halls", amenities: "/amenities",
  };
  return <Navigate to={routes[fallback] || "/help"} replace />;
}
